import { useMemo, useState, type ReactNode } from 'react';
import type { DateRange } from 'react-day-picker';
import { differenceInDays, startOfMonth, endOfMonth, format, subDays } from 'date-fns';
import { useAuth } from '@/hooks';
import { usePermissions } from '@/hooks/use-permissions';
import { useMetricSeries } from '@/hooks/use-analytics-v2';
import {
  DashboardHeader,
  OverviewSection,
  TicketMetricsSection,
  TicketBreakdownSection,
  MetricChart,
  ChartMetricSelector,
  GranularitySelector,
  metricDefs,
  isMetricSeriesAvailable,
  useChartGranularity,
} from '@/modules/analytics-v2';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ScalarMetricKey, SeriesMetricKey } from '@/types/analyticsV2';

type DashboardTab = 'message' | 'ticket';

const DASHBOARD_TABS: { value: DashboardTab; label: string }[] = [
  { value: 'message', label: 'Message Metrics' },
  { value: 'ticket', label: 'Ticket metrics' },
];

/**
 * Build the same prior-period window the backend uses (immediately before
 * the current range, same length) and return both the YYYY-MM-DD strings
 * and a human-readable label for the trend tooltip.
 */
const buildComparison = (dateRange: DateRange | undefined): { label?: string } => {
  if (!dateRange?.from || !dateRange?.to) return {};
  const lengthDays = differenceInDays(dateRange.to, dateRange.from) + 1;
  const prevTo = subDays(dateRange.from, 1);
  const prevFrom = subDays(prevTo, lengthDays - 1);
  return {
    label: `${format(prevFrom, 'd MMM, yy')} – ${format(prevTo, 'd MMM, yy')}`,
  };
};

export default function AnalyticsV2({ switcher }: { switcher?: ReactNode } = {}) {
  const { displayName } = useAuth();
  const { can } = usePermissions();
  const canViewAnalytics = can('view', 'analytics');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [activeTab, setActiveTab] = useState<DashboardTab>('message');
  const [chartMetric, setChartMetric] = useState<ScalarMetricKey>('aiMessages');

  const fromDate = canViewAnalytics && dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '';
  const toDate = canViewAnalytics && dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '';

  const [granularity, setGranularity] = useChartGranularity(fromDate, toDate);
  const comparison = useMemo(() => buildComparison(dateRange), [dateRange]);

  // Guard: if the metric currently driving the chart loses its series support
  // (eg. metric-defs edit during dev), fall back to aiMessages.
  const safeChartMetric: ScalarMetricKey = isMetricSeriesAvailable(chartMetric)
    ? chartMetric
    : 'aiMessages';
  const chartDef = metricDefs[safeChartMetric];
  const activeSeriesKey = chartDef.chart?.seriesKey;
  const seriesKeys = useMemo<SeriesMetricKey[]>(
    () => (activeSeriesKey ? [activeSeriesKey] : []),
    [activeSeriesKey],
  );

  const seriesQuery = useMetricSeries(seriesKeys, fromDate, toDate, granularity);
  const activeSeries = activeSeriesKey ? seriesQuery.data?.[activeSeriesKey] : undefined;

  if (!canViewAnalytics) {
    return (
      <div className='flex flex-col gap-4 p-6'>
        <h1 className='text-base font-semibold'>Welcome back, {displayName}</h1>
        {switcher}
        <p className='text-sm text-muted-foreground'>
          Use the sidebar to access the tools available to you.
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6 p-4'>
      <DashboardHeader
        userName={displayName}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        subtitle={switcher}
      />

      <div className='self-start'>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DashboardTab)} variant='segmented'>
          <TabsList>
            {DASHBOARD_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {activeTab === 'message' ? (
        <div className='flex flex-col gap-6'>
          <OverviewSection
            fromDate={fromDate}
            toDate={toDate}
            comparisonLabel={comparison.label}
          />
          <div className='flex flex-col gap-3'>
            <h2 className='text-[12px] text-gray-800 font-medium'>Trends</h2>
            <MetricChart
              def={chartDef}
              series={activeSeries}
              isLoading={seriesQuery.isLoading}
              selector={
                <ChartMetricSelector value={safeChartMetric} onChange={setChartMetric} />
              }
              controls={<GranularitySelector value={granularity} onChange={setGranularity} />}
            />
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-6'>
          <TicketMetricsSection
            fromDate={fromDate}
            toDate={toDate}
            comparisonLabel={comparison.label}
          />
          <TicketBreakdownSection fromDate={fromDate} toDate={toDate} />
        </div>
      )}
    </div>
  );
}
